/*
 * Public contract (unchanged from the original lib/DataTable.jsx -- 28 consumers
 * depend on this shape, so it is now enforced by TypeScript instead of a comment).
 * https://www.notion.so/Build-re-usable-datatable-component-05b0f15d8e7840f1a3cd6c1c8366db50
 *
 * `col.sx` compatibility note: the original passed this straight into MUI's `sx`
 * prop on the cell's <TableCell>. There is no Tailwind/shadcn equivalent runtime,
 * so it's honored via a small best-effort shim (`sxToStyle` below) that:
 *   - maps the MUI spacing shorthands actually used by consumers today (m/p +
 *     t/r/b/l/x/y) to real CSS properties, using MUI's default 8px-per-unit scale
 *     for numeric values (e.g. `{ pr: 0 }` -> `paddingRight: 0`, `{ mr: -3 }` ->
 *     `marginRight: -24 }`);
 *   - passes through any other key as-is as an inline style (covers
 *     `fontWeight`, `fontSize`, `textAlign`, `maxWidth`, which are already valid
 *     camelCase CSS properties and are the only other keys in use).
 * This is NOT general MUI `sx` support: it does not implement theme-callback
 * values (`sx={(theme) => ...}`), responsive breakpoint objects
 * (`{ display: { xs: 'none', md: 'block' } }`), or nested selectors (`&:hover`).
 * Phase 0 confirmed all 241 `sx` usages app-wide are flat object literals, and
 * the only two `col.sx` call sites today (VideoViewer.jsx, HomepageTournaments.jsx)
 * use exactly the keys this shim covers, so current behavior is preserved
 * exactly -- but a future column def using a breakpoint object or theme callback
 * would silently lose styling. Flagging explicitly per Phase 5 instructions
 * rather than pretending this is a full `sx` polyfill.
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from 'cn';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import VideoMenu from './VideoMenu';
import VideoDetailsMenu from 'components/VideoDetailsDialog';

export interface DataTableColumn<T = any> {
    id: string;
    name: ReactNode;
    valueAccessor?: (row: T) => ReactNode;
    sortAccessor?: (row: T) => any;
    navigateOnClick?: (row: T) => string;
    onClick?: (row: T) => void;
    width?: number | string;
    /** Tailwind classes for this column's cells. Preferred over `sx`. */
    className?: string;
    /** Legacy MUI-shaped style object. See the file-level comment above for
     *  exactly what is and isn't supported; prefer `className` for new code. */
    sx?: Record<string, unknown>;
    backgroundColor?: (row: T) => string;
    /** Accepted for column-def compatibility; unused internally (also true of the
     *  original -- e.g. components/Popular.jsx sets `sort: true` on every column,
     *  and DataTable.jsx never read it). */
    sort?: boolean;
}

export interface DataTableProps<T = any> {
    columns: DataTableColumn<T>[];
    rows: T[];
    order?: 'asc' | 'desc';
    /** A single column def, or an array of them for multi-key sort precedence. */
    sortBy?: DataTableColumn<T> | DataTableColumn<T>[];
    useContainer?: boolean;
    noDataMessage?: ReactNode;
    navigateOnRowClick?: (row: T) => string;
    videoMenu?: boolean;
    sortHeaderMarginRight?: number | string;
}

const MUI_SPACING_PX = 8;

const SPACING_KEYS: Record<string, string[]> = {
    m: ['margin'],
    mt: ['marginTop'],
    mr: ['marginRight'],
    mb: ['marginBottom'],
    ml: ['marginLeft'],
    mx: ['marginLeft', 'marginRight'],
    my: ['marginTop', 'marginBottom'],
    p: ['padding'],
    pt: ['paddingTop'],
    pr: ['paddingRight'],
    pb: ['paddingBottom'],
    pl: ['paddingLeft'],
    px: ['paddingLeft', 'paddingRight'],
    py: ['paddingTop', 'paddingBottom']
};

function resolveSpacingValue(value: unknown): unknown {
    return typeof value === 'number' ? value * MUI_SPACING_PX : value;
}

function sxToStyle(sx?: Record<string, unknown>): React.CSSProperties {
    if (!sx) return {};
    const style: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(sx)) {
        const cssProps = SPACING_KEYS[key];
        if (cssProps) {
            for (const cssProp of cssProps) {
                style[cssProp] = resolveSpacingValue(value);
            }
        } else {
            style[key] = value;
        }
    }
    return style as React.CSSProperties;
}

const extractValue = (item: any, col: any) => {
    return col?.sortAccessor
        ? col.sortAccessor(item)
        : col?.valueAccessor
          ? col.valueAccessor(item).toLowerCase()
          : item[col.id];
};

const sortFn = (cols: any, order: 'asc' | 'desc') => {
    if (!cols) {
        return () => 0;
    }

    if (!Array.isArray(cols)) {
        cols = [cols];
    }

    return (a: any, b: any) => {
        let outcome = 0;
        for (let i = 0; i < cols.length; i++) {
            const col = cols[i];
            const aValue = extractValue(a, col);
            const bValue = extractValue(b, col);
            if (aValue > bValue) {
                outcome = 1;
                break;
            } else if (aValue < bValue) {
                outcome = -1;
                break;
            }
        }

        return order === 'desc' ? -outcome : outcome;
    };
};

export default function DataTable<T extends Record<string, any> = any>(props: DataTableProps<T>) {
    const navigate = useNavigate();
    const [order, setOrder] = useState<'asc' | 'desc'>(props.order || 'asc');
    const [sortBy, setSortBy] = useState<DataTableColumn<T> | DataTableColumn<T>[] | undefined>();
    const useContainer = props.useContainer ?? true;
    useEffect(() => setSortBy(props.sortBy), [props.sortBy]);

    const onRequestSort = (column: DataTableColumn<T>) => {
        const isAsc = sortBy === column && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setSortBy(column);
    };

    const sortHeaderStyle: React.CSSProperties = {
        marginRight: (props.sortHeaderMarginRight !== undefined
            ? resolveSpacingValue(props.sortHeaderMarginRight)
            : undefined) as React.CSSProperties['marginRight']
    };

    // Numeric/score columns set `text-center`/`text-right` in their `className`
    // for the body cells (e.g. Pool.tsx's W/L, Pools.tsx's Score columns) so
    // digits line up instead of hugging the left edge. Mirroring just the
    // alignment token onto the header keeps the column head over its values
    // without pulling in the rest of a body-only className (font sizing, etc).
    const ALIGN_CLASS_RE = /\btext-(left|center|right)\b/;

    const colHeaderCells = props.columns.map((col) => {
        // Mirrors the original: `active`/`direction` compare against whatever
        // `sortBy` currently holds (a column object, or an array of them for
        // multi-key sort), by reference -- same semantics as before, bugs
        // included (e.g. an array `sortBy` never reference-equals a single
        // column, so no header ever shows "active" in that case).
        const isActive = sortBy === col;
        const align = col.className?.match(ALIGN_CLASS_RE)?.[1];
        return (
            <TableHead
                key={col.id}
                className={cn(
                    align === 'center' && 'text-center',
                    align === 'right' && 'text-right'
                )}
                aria-sort={isActive ? (order === 'desc' ? 'descending' : 'ascending') : undefined}>
                <button
                    type="button"
                    className={cn(
                        'inline-flex items-center gap-1 font-bold text-foreground',
                        'hover:text-foreground/80 focus-visible:outline-none',
                        align === 'center' && 'w-full justify-center',
                        align === 'right' && 'w-full justify-end'
                    )}
                    style={sortHeaderStyle}
                    onClick={() => onRequestSort(col)}>
                    {col.name}
                    <ChevronDown
                        className={cn(
                            'h-3.5 w-3.5 shrink-0 transition-transform',
                            isActive ? 'opacity-100' : 'opacity-0',
                            order === 'asc' && 'rotate-180'
                        )}
                    />
                </button>
            </TableHead>
        );
    });

    const rows = props.rows ? props.rows.slice() : [];
    const table = (
        <Table className="min-w-[200px] text-xs sm:text-sm">
            <TableHeader>
                <TableRow>{colHeaderCells}</TableRow>
            </TableHeader>
            <TableBody>
                {rows.length ? (
                    rows.sort(sortFn(sortBy, order)).map((row: any, index: number) => {
                        const cells = props.columns.map((col) => {
                            const clickable = !!(col.navigateOnClick || col.onClick);
                            const hasBackground =
                                !!col.backgroundColor && col.backgroundColor(row) !== 'inherit';
                            return (
                                <TableCell
                                    key={col.id}
                                    className={cn(
                                        'whitespace-normal',
                                        clickable && 'cursor-pointer hover:text-primary-dark',
                                        col.className
                                    )}
                                    style={{
                                        width: col.width || 'auto',
                                        ...sxToStyle(col.sx)
                                    }}
                                    onClick={(e) => {
                                        if (!clickable) return;
                                        e.stopPropagation();
                                        const path = col.navigateOnClick?.(row);
                                        if (path) {
                                            navigate(path);
                                        } else if (col.onClick) {
                                            col.onClick(row);
                                        }
                                    }}>
                                    <span
                                        style={
                                            hasBackground
                                                ? {
                                                      display: 'inline-block',
                                                      padding: '2px 8px',
                                                      borderRadius: 4,
                                                      boxShadow:
                                                          'inset 0 0 20px ' +
                                                          col.backgroundColor!(row)
                                                  }
                                                : undefined
                                        }>
                                        {col.valueAccessor ? col.valueAccessor(row) : row[col.id]}
                                    </span>
                                </TableCell>
                            );
                        });
                        if (row?.videos) {
                            cells.push(
                                <td key={row.id + '-videos'} className="relative">
                                    <VideoMenu
                                        className="absolute top-0 right-2 flex h-full flex-col justify-center"
                                        videos={row.videos}
                                    />
                                </td>
                            );
                        }

                        if (props?.videoMenu) {
                            cells.push(
                                <td key={row.id + '-report'} className="relative">
                                    <VideoDetailsMenu
                                        video={row}
                                        sx={{
                                            position: 'absolute',
                                            top: '0px',
                                            right: '8px',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}
                                    />
                                </td>
                            );
                        }

                        return (
                            <TableRow
                                className={cn(
                                    'relative',
                                    props.navigateOnRowClick
                                        ? 'cursor-pointer hover:bg-muted/50'
                                        : '[&:hover]:bg-transparent'
                                )}
                                onClick={
                                    props.navigateOnRowClick
                                        ? () => navigate(props.navigateOnRowClick!(row))
                                        : undefined
                                }
                                key={row.id || index}>
                                {cells}
                            </TableRow>
                        );
                    })
                ) : (
                    <TableRow>
                        <TableCell>
                            {props.noDataMessage ? props.noDataMessage : 'No data available'}
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );

    return useContainer ? (
        <div className="mb-6 overflow-x-auto rounded-md border bg-card shadow-xs sm:mb-8">{table}</div>
    ) : (
        table
    );
}
